import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStylingAssistant } from '../hooks/useStylingAssistant';

// ─── Context Shape ───────────────────────────────────────────────────────────

interface StylingAssistantContextValue {
  /** All state & actions from the core hook */
  sa: ReturnType<typeof useStylingAssistant>;

  /** Whether the floating popup panel is open */
  isPanelOpen: boolean;
  /** Open the floating popup panel */
  openPanel: () => void;
  /** Close the floating popup panel (clears chat) */
  closePanel: () => void;
  /** Close the floating popup panel WITHOUT clearing chat (for expand) */
  closePanelSilently: () => void;

  /**
   * Expand the popup into the full-page view.
   * Closes the popup, navigates to /style-assistant, preserves chat.
   */
  expandToFullPage: () => void;

  /**
   * Collapse the full page back to the popup view.
   * Navigates back to the previous page and re-opens the popup.
   */
  collapseToPopup: () => void;

  /** Whether the current full-page view was reached via expand (vs direct nav) */
  isExpandedFromPopup: boolean;

  /** Track where the user came from so we can navigate back */
  previousPath: string | null;
}

const StylingAssistantCtx = createContext<StylingAssistantContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export const StylingAssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const sa = useStylingAssistant();
  const navigate = useNavigate();
  const location = useLocation();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [previousPath, setPreviousPath] = useState<string | null>(null);
  const [isExpandedFromPopup, setIsExpandedFromPopup] = useState(false);

  // Use a ref to track the latest location for expand, avoiding stale closures
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    sa.handleRefresh();
  }, [sa]);

  const closePanelSilently = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const expandToFullPage = useCallback(() => {
    // Remember current path so we can navigate back on collapse
    const currentPath = locationRef.current;
    setPreviousPath(currentPath);
    setIsExpandedFromPopup(true);
    // Close the popup panel (no refresh — keep chat intact)
    setIsPanelOpen(false);
    // Navigate to the full page
    navigate('/style-assistant');
  }, [navigate]);

  const collapseToPopup = useCallback(() => {
    // Navigate back to where the user was before expanding
    const target = previousPath && previousPath !== '/style-assistant' ? previousPath : '/fabric';
    setIsExpandedFromPopup(false);
    setPreviousPath(null);
    navigate(target);
    // Re-open the popup panel with existing chat after a small delay
    // to allow navigation to complete
    setTimeout(() => {
      setIsPanelOpen(true);
    }, 50);
  }, [previousPath, navigate]);

  return (
    <StylingAssistantCtx.Provider
      value={{
        sa,
        isPanelOpen,
        openPanel,
        closePanel,
        closePanelSilently,
        expandToFullPage,
        collapseToPopup,
        isExpandedFromPopup,
        previousPath,
      }}
    >
      {children}
    </StylingAssistantCtx.Provider>
  );
};

// ─── Consumer Hook ───────────────────────────────────────────────────────────

export function useStylingAssistantContext() {
  const ctx = useContext(StylingAssistantCtx);
  if (!ctx) {
    throw new Error(
      'useStylingAssistantContext must be used within a <StylingAssistantProvider>.'
    );
  }
  return ctx;
}
